/**
 * Brand Provider Component
 * Provides brand context and injects CSS variables for theming
 */

import React, { createContext, useContext, useEffect } from 'react';
import brand, { generateCSSVariables } from '../config/brand';

// Create Brand Context
const BrandContext = createContext(brand);

// Custom hook to use brand context
export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

// Brand Provider Component
export const BrandProvider = ({ children }) => {
  useEffect(() => {
    // Inject CSS variables into document root
    const cssVars = generateCSSVariables();
    const root = document.documentElement;
    
    Object.entries(cssVars).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Update document title with brand name
    document.title = brand.seo.defaultTitle;
    
    // Update favicon if brand-specific
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon && brand.favicon) {
      favicon.href = brand.favicon;
    }
    
    // Add brand class to body for CSS targeting
    document.body.classList.add(`brand-${brand.id}`);
    
    return () => {
      document.body.classList.remove(`brand-${brand.id}`);
    };
  }, []);

  return (
    <BrandContext.Provider value={brand}>
      {children}
    </BrandContext.Provider>
  );
};

export default BrandProvider;
